import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import api from '../utils/api'

interface User {
  id: string
  email: string
  tfa_enabled: boolean
  tfa_verified: boolean
}

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<{ requires_tfa: boolean }>
  verifyTFA: (code: string) => Promise<void>
  logout: () => void
  register: (email: string, password: string) => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()

  // Get redirect path from location state (preserved from ProtectedRoute) or query param (from api.ts)
  const searchParams = new URLSearchParams(location.search)
  const redirectPath = (location.state as { from?: string })?.from
    || searchParams.get('redirect')
    || '/dashboard'

  useEffect(() => {
    checkAuth()
  }, [])

  async function checkAuth() {
    try {
      const token = localStorage.getItem('access_token')
      if (!token) {
        setLoading(false)
        return
      }

      const response = await api.get('/auth/me')
      setUser(response.data)
    } catch {
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
    } finally {
      setLoading(false)
    }
  }

  async function login(email: string, password: string) {
    const response = await api.post('/auth/login', { email, password })

    if (response.data.requires_tfa) {
      // Store temporary token for TFA verification
      localStorage.setItem('tfa_token', response.data.tfa_token)
      return { requires_tfa: true }
    }

    // No TFA yet - redirect to setup
    localStorage.setItem('access_token', response.data.access_token)
    localStorage.setItem('refresh_token', response.data.refresh_token)
    setUser(response.data.user)

    if (!response.data.user.tfa_enabled) {
      navigate('/setup-tfa')
    }

    return { requires_tfa: false }
  }

  async function verifyTFA(code: string) {
    const tfaToken = localStorage.getItem('tfa_token')
    const response = await api.post('/auth/verify-tfa', {
      tfa_token: tfaToken,
      code
    })

    localStorage.removeItem('tfa_token')
    localStorage.setItem('access_token', response.data.access_token)
    localStorage.setItem('refresh_token', response.data.refresh_token)
    setUser({ ...response.data.user, tfa_verified: true })
    // Redirect to the original destination (preserving query params like ?order=)
    navigate(redirectPath)
  }

  async function register(email: string, password: string) {
    const response = await api.post('/auth/register', { email, password })
    localStorage.setItem('access_token', response.data.access_token)
    localStorage.setItem('refresh_token', response.data.refresh_token)
    setUser(response.data.user)
    // Always redirect to TFA setup after registration
    navigate('/setup-tfa')
  }

  function logout() {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('tfa_token')
    setUser(null)
    navigate('/')
  }

  async function refreshUser() {
    try {
      const response = await api.get('/auth/me')
      setUser(response.data)
    } catch {
      // Ignore errors
    }
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyTFA, logout, register, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
